
<template>
  <div class="roles-page">
    <!-- 顶部导航 -->
    <header class="header">
      <div class="header-left" @click="goBack">
        <span class="back-icon">←</span>
        <span class="header-title">{{ $t('admin.Roles.line8_text') }}</span>
      </div>
      <button class="header-icon" @click="handleLogout">🚪</button>
    </header>

    <!-- 统计卡片 -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-value">{{ roleList.length }}</div>
        <div class="stat-label">{{ $t('admin.Roles.line17_text') }}</div>
      </div>
      <div
        class="stat-card"
        style="background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);"
      >
        <div class="stat-value">{{ allFeatures.length }}</div>
        <div class="stat-label">{{ $t('admin.Roles.line24_text') }}</div>
      </div>
      <div
        class="stat-card"
        style="background: linear-gradient(135deg, #faad14 0%, #ff4d4f 100%);"
      >
        <div class="stat-value">{{ selectedFeatures.length }}</div>
        <div class="stat-label">{{ $t('admin.Roles.line31_text') }}</div>
      </div>
    </div>

    <!-- 角色选择器 -->
    <div class="role-selector">
      <div
        v-for="role in roleList"
        :key="role.id"
        :class="['role-chip', { active: selectedRole && selectedRole.id === role.id }]"
        @click="selectRole(role)"
      >
        <span class="role-chip-icon">{{ getRoleIcon(role.code) }}</span>
        <span>{{ role.name }}</span>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner">⏳</div>
      <div class="loading-text">{{ $t('common.loading') }}</div>
    </div>

    <!-- 功能点配置 -->
    <div v-else-if="selectedRole" class="feature-config">
      <div
        v-for="(features, module) in featuresByModule"
        :key="module"
        class="feature-section"
      >
        <div class="feature-section-title">
          <span>{{ getModuleIcon(module) }}</span>
          <span>{{ module }}</span>
        </div>
        <div class="feature-list">
          <div
            v-for="feature in features"
            :key="feature.code"
            class="feature-item"
          >
            <input
              type="checkbox"
              :id="feature.code"
              v-model="selectedFeatures"
              :value="feature.code"
            />
            <label :for="feature.code">{{ feature.name }}</label>
          </div>
        </div>
      </div>

      <button
        class="btn btn-primary btn-block"
        @click="saveRoleFeaturesHandler"
        :disabled="saving"
      >
        {{ saving ? '💾 保存中...' : '💾 保存配置' }}
      </button>
    </div>

    <!-- 空状态：角色为空 -->
    <div
      v-if="!loading && roleList.length === 0"
      class="empty-state"
    >
      <div class="empty-icon">📭</div>
      <div class="empty-text">{{ $t('admin.Roles.line97_text') }}</div>
    </div>

    <!-- 空状态：有角色但没选中（极少见） -->
    <div
      v-if="!loading && roleList.length > 0 && !selectedRole"
      class="empty-state"
    >
      <div class="empty-icon">⚠️</div>
      <div class="empty-text">{{ $t('admin.Roles.line106_text') }}</div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <div class="nav-item" @click="goToPage('/admin/dashboard')">
        <div class="nav-icon">🏠</div>
        <div class="nav-label">{{ $t('common.home') }}</div>
      </div>
      <div class="nav-item" @click="goToPage('/admin/users')">
        <div class="nav-icon">👥</div>
        <div class="nav-label">{{ $t('admin.Roles.line117_text') }}</div>
      </div>
      <div class="nav-item active" @click="goToPage('/admin/roles')">
        <div class="nav-icon">🔐</div>
        <div class="nav-label">{{ $t('admin.Roles.line121_text') }}</div>
      </div>
      <div class="nav-item" @click="goToPage('/admin/features')">
        <div class="nav-icon">⚙️</div>
        <div class="nav-label">{{ $t('admin.Roles.line125_text') }}</div>
      </div>
    </nav>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { logout } from '@/utils/auth';
import {
  getRoles,
  getRoleFeatures,
  saveRoleFeatures,
  getFeatures
} from '@/services/api';

export default {
  name: 'RoleConfig',
  data() {
    return {
      loading: false,
      saving: false,
      roleList: [],
      selectedRole: null,
      selectedFeatures: [],
      allFeatures: [],
      featuresByModule: {}
    };
  },
  computed: {
    ...mapGetters(['userName'])
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    async handleLogout() {
      await logout();
    },

    goToPage(path) {
      if (this.$route.path === path) {
        return;
      }
      this.$router.push(path).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('路由跳转失败:', err);
        }
      });
    },

    async loadData() {
      console.log('开始加载角色数据...');
      this.loading = true;
      try {
        console.log('调用 getRoles API...');
        const rolesRes = await getRoles();
        console.log('角色数据:', rolesRes);
        this.roleList = rolesRes || [];

        console.log('调用 getFeatures API...');
        const featuresRes = await getFeatures();
        console.log('功能点数据:', featuresRes);
        this.allFeatures = featuresRes || [];

        this.featuresByModule = this.allFeatures.reduce((acc, feature) => {
          const moduleKey = feature.module || '未分类';
          if (!acc[moduleKey]) {
            acc[moduleKey] = [];
          }
          acc[moduleKey].push(feature);
          return acc;
        }, {});

        console.log('角色列表:', this.roleList);
        if (this.roleList.length > 0) {
          this.selectRole(this.roleList[0]);
        } else {
          console.warn('没有角色数据！');
        }
      } catch (error) {
        console.error('加载数据失败:', error);
        this.showToast('加载失败：' + error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    async selectRole(role) {
      this.selectedRole = role;
      this.selectedFeatures = [];

      try {
        const featuresRes = await getRoleFeatures(role.id);
        // 后端如果返回的是字符串数组（featureCode 列表），这里直接赋值
        this.selectedFeatures = featuresRes || [];
      } catch (error) {
        console.error('加载角色功能点失败:', error);
        this.showToast('加载角色功能点失败：' + error.message, 'error');
      }
    },

    async saveRoleFeaturesHandler() {
      if (!this.selectedRole) {
        this.showToast('请选择角色', 'error');
        return;
      }

      this.saving = true;
      try {
        await saveRoleFeatures(this.selectedRole.id, this.selectedFeatures);
        this.showToast('保存成功', 'success');
      } catch (error) {
        console.error('保存失败:', error);
        this.showToast('保存失败：' + error.message, 'error');
      } finally {
        this.saving = false;
      }
    },

    getRoleIcon(roleCode) {
      const iconMap = {
        ADMIN: '👑',
        STALL: '🏪',
        WHOLESALER: '📦',
        DRIVER: '🚚'
      };
      return iconMap[roleCode] || '👤';
    },

    getModuleIcon(module) {
      const iconMap = {
        '档口管理': '🏪',
        '商品管理': '📦',
        '订单管理': '📋',
        '配送管理': '🚚',
        '财务管理': '💰',
        '用户管理': '👥',
        '系统管理': '⚙️'
      };
      return iconMap[module] || '📌';
    },

    goBack() {
      this.$router.back();
    },

    showToast(message, type = 'info') {
      const toast = document.createElement('div');
      toast.className = `toast toast-${type}`;
      toast.textContent = message;
      toast.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: ${
          type === 'error'
            ? 'rgba(255,77,79,0.9)'
            : type === 'success'
            ? 'rgba(52,199,89,0.9)'
            : 'rgba(0,0,0,0.7)'
        };
        color: #fff;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        z-index: 99999;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      `;
      document.body.appendChild(toast);
      setTimeout(() => {
        toast.style.transition = 'opacity 0.3s';
        toast.style.opacity = '0';
        setTimeout(() => toast.remove(), 300);
      }, 3000);
    }
  }
};
</script>

<style scoped>
.roles-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 16px 16px 140px; /* 增加底部间距，避免被底部导航遮挡 */
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.back-icon {
  font-size: 20px;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
}

.header-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.2);
  border: none;
  font-size: 18px;
  cursor: pointer;
  border-radius: 50%;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 12px;
}

.stat-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 14px 12px;
  border-radius: 10px;
  text-align: center;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 11px;
  opacity: 0.9;
}

.role-selector {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 12px;
  margin-bottom: 12px;
  -webkit-overflow-scrolling: touch;
}

.role-chip {
  flex-shrink: 0;
  padding: 8px 16px;
  background: white;
  border: 2px solid #e8e8e8;
  border-radius: 20px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.role-chip.active {
  border-color: #667eea;
  background: #f9f0ff;
  color: #667eea;
}

.role-chip-icon {
  font-size: 16px;
}

.loading-state {
  text-align: center;
  padding: 60px 16px;
}

.loading-spinner {
  font-size: 48px;
  margin-bottom: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 16px;
  color: #999;
}

.feature-config {
  padding: 0 12px;
}

.feature-section {
  margin-bottom: 20px;
}

.feature-section-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 12px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;
}

.feature-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: #fafafa;
  border-radius: 8px;
}

.feature-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #667eea;
}

.feature-item label {
  font-size: 13px;
  flex: 1;
}

.btn {
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-block {
  width: 100%;
  margin-top: 16px;
  padding: 14px;
  font-size: 15px;
}

.empty-state {
  text-align: center;
  padding: 40px 16px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.empty-text {
  font-size: 14px;
  color: #999;
}

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  display: flex;
  border-top: 1px solid #e0e0e0;
  padding-bottom: env(safe-area-inset-bottom);
}

.nav-item {
  flex: 1;
  text-align: center;
  padding: 8px 0;
  cursor: pointer;
}

.nav-item.active {
  color: #667eea;
}

.nav-icon {
  font-size: 20px;
  margin-bottom: 2px;
}

.nav-label {
  font-size: 12px;
}
</style>
